import React, { useState, useEffect } from 'react';
import { CaretLeft, CaretRight, X, MagnifyingGlassPlus } from 'phosphor-react';
import { getImage } from '../assets/images';
import './ProductImageCarousel.css';

const ProductImageCarousel = ({ images = [], productName = 'Produto' }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isZoomOpen, setIsZoomOpen] = useState(false);

  // Preparar lista de imagens
  const imageList = React.useMemo(() => {
    if (images && images.length > 0) {
      // Ordenar por display_order e usar imagens do banco
      return images
        .sort((a, b) => a.display_order - b.display_order)
        .map(img => ({
          url: getImage(img.image_url),
          alt: `${productName} - Imagem ${img.display_order + 1}`,
          isPrimary: img.is_primary
        }));
    }
    // Fallback para imagem padrão
    return [{
      url: getImage('camara.jpg'),
      alt: productName,
      isPrimary: true
    }];
  }, [images, productName]);

  // Reset index quando mudarem as imagens
  useEffect(() => {
    setCurrentImageIndex(0);
    setIsLoading(false);
  }, [images]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === imageList.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? imageList.length - 1 : prev - 1
    );
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  // Funções para o modal de zoom
  const openZoom = () => {
    setIsZoomOpen(true);
    document.body.style.overflow = 'hidden'; // Impedir scroll do body
  };

  const closeZoom = () => {
    setIsZoomOpen(false);
    document.body.style.overflow = 'unset'; // Restaurar scroll do body
  };

  // Navegação no zoom
  const nextZoomImage = (e) => {
    if (e) e.stopPropagation();
    nextImage();
  };

  const prevZoomImage = (e) => {
    if (e) e.stopPropagation();
    prevImage();
  };

  // Fechar modal com ESC e navegação com setas
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isZoomOpen) {
        if (e.key === 'Escape') {
          closeZoom();
        } else if (e.key === 'ArrowLeft') {
          prevZoomImage();
        } else if (e.key === 'ArrowRight') {
          nextZoomImage();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset'; // Cleanup
    };
  }, [isZoomOpen, imageList.length]);

  if (isLoading) {
    return (
      <div className="product-carousel-container">
        <div className="carousel-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-carousel-container">
      {/* Imagem principal */}
      <div className="carousel-main-image">
        <img
          src={imageList[currentImageIndex]?.url}
          alt={imageList[currentImageIndex]?.alt}
          className="main-image clickable"
          onClick={openZoom}
          title="Clique para ampliar"
        />
        
        {/* Botão de zoom */}
        <button 
          className="zoom-btn" 
          onClick={openZoom}
          aria-label="Ampliar imagem"
          title="Ampliar imagem"
        >
          <MagnifyingGlassPlus size={20} weight="bold" />
        </button>
        
        {/* Botões de navegação (só aparecem se houver mais de 1 imagem) */}
        {imageList.length > 1 && (
          <>
            <button 
              className="carousel-btn carousel-btn-prev" 
              onClick={prevImage}
              aria-label="Imagem anterior"
            >
              <CaretLeft size={24} weight="bold" />
            </button>
            <button 
              className="carousel-btn carousel-btn-next" 
              onClick={nextImage}
              aria-label="Próxima imagem"
            >
              <CaretRight size={24} weight="bold" />
            </button>
          </>
        )}

        {/* Indicador de posição */}
        {imageList.length > 1 && (
          <div className="carousel-indicator">
            {currentImageIndex + 1} / {imageList.length}
          </div>
        )}
      </div>

      {/* Thumbnails (só aparecem se houver mais de 1 imagem) */}
      {imageList.length > 1 && (
        <div className="carousel-thumbnails">
          {imageList.map((image, index) => (
            <button
              key={`thumbnail-${index}-${image.url}`}
              className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
              onClick={() => goToImage(index)}
              aria-label={`Ver imagem ${index + 1}`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="thumbnail-image"
              />
              {image.isPrimary && (
                <div className="primary-badge">Principal</div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Navegação por teclado */}
      <div 
        className="carousel-keyboard-nav"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') prevImage();
          if (e.key === 'ArrowRight') nextImage();
        }}
        aria-label="Use as setas do teclado para navegar pelas imagens"
      />

      {/* Modal de Zoom com Navegação */}
      {isZoomOpen && (
        <div className="zoom-modal-overlay" onClick={closeZoom}>
          <div className="zoom-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="zoom-close-btn" onClick={closeZoom}>
              <X size={32} weight="bold" />
            </button>
            
            <div className="zoom-image-container">
              <img
                src={imageList[currentImageIndex]?.url}
                alt={imageList[currentImageIndex]?.alt}
                className="zoom-image"
              />
              
              {/* Setas de navegação no zoom (só aparecem se houver mais de 1 imagem) */}
              {imageList.length > 1 && (
                <>
                  <button 
                    className="zoom-nav-btn zoom-nav-prev" 
                    onClick={prevZoomImage}
                    aria-label="Imagem anterior"
                  >
                    <CaretLeft size={32} weight="bold" />
                  </button>
                  <button 
                    className="zoom-nav-btn zoom-nav-next" 
                    onClick={nextZoomImage}
                    aria-label="Próxima imagem"
                  >
                    <CaretRight size={32} weight="bold" />
                  </button>
                  
                  {/* Indicador de posição no zoom */}
                  <div className="zoom-indicator">
                    {currentImageIndex + 1} / {imageList.length}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductImageCarousel;
